import React from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { REGISTER_URL } from '../lib/authLinks';

const TradingDetailPage = () => {
  const { option } = useParams();

  const tradingContent = {
    'how-to-trade': {
      title: 'How to Trade',
      description: 'Learn the basics of trading with PreciseFx',
      details: [
        { step: '1', title: 'Create Your Account', text: 'Sign up in less than 2 minutes with your email and verify your identity.' },
        { step: '2', title: 'Choose Your Asset', text: 'Select from forex, stocks, commodities, indices, or cryptocurrencies.' },
        { step: '3', title: 'Let AI Analyze', text: 'PreciseFx\'s advanced algorithms analyze market conditions automatically.' },
        { step: '4', title: 'AI Executes Trades', text: 'Our AI automatically executes trades with optimal timing — no manual entries required.' },
        { step: '5', title: 'Monitor & Close', text: 'Track your open positions and close them when you reach your profit target.' }
      ]
    },
    'stocks': {
      title: 'Stocks Trading',
      description: 'Trade blue-chip stocks with zero commission',
      details: [
        { title: 'Zero Commission', text: 'Trade stocks without paying any commission fees. Keep more of your profits.' },
        { title: '100+ Stocks', text: 'Access major companies like Apple, Google, Amazon, Tesla, and more.' },
        { title: 'Real-time Quotes', text: 'Get live market data and execute trades instantly.' },
        { title: 'Fractional Shares', text: 'Start trading with as little as $10, no need to buy full shares.' },
        { title: 'Global Markets', text: 'Trade stocks from US, European, and Asian markets.' }
      ]
    },
    'demo-account': {
      title: 'AI Auto Trading',
      description: 'Trade automatically using advanced AI technology',
      subtitle: 'Experience stress-free trading with intelligent automation designed to maximize results and minimize effort.',
      details: [
        { icon: '🚀', title: 'Smart Trading Algorithms', text: 'Our AI monitors market trends 24/7 and executes trades with precision — no emotions, no delays.' },
        { icon: '📊', title: 'Real Market Execution', text: 'Every trade is executed in real markets with live pricing and high-speed order processing.' },
        { icon: '🔒', title: 'Risk-Managed Strategies', text: 'Built-in safety measures help protect your capital using proven risk-management techniques.' },
        { icon: '⚙️', title: 'Full Control, Zero Effort', text: 'Set your preferences once — the AI handles the rest while you track your performance anytime.' },
        { icon: '⚡', title: 'One-Click Activation', text: 'Enable AI trading instantly with a single click and start earning without active involvement.' }
      ]
    },
    'india': {
      title: 'PreciseFx for India',
      description: 'Special benefits for Indian traders',
      details: [
        { title: 'Local Payment Methods', text: 'Deposit and withdraw using UPI, Paytm, PhonePe, and Indian bank transfers.' },
        { title: 'INR Support', text: 'Trade directly in Indian Rupees without currency conversion fees.' },
        { title: 'Indian Market Hours', text: 'Trade during convenient hours aligned with Indian time zones.' },
        { title: 'Local Support', text: '24/7 customer support in Hindi and English.' },
        { title: 'Compliance', text: 'Fully compliant with Indian trading regulations and guidelines.' }
      ]
    },
    'fixed-time': {
      title: 'AI-Powered Smart Trading',
      description: 'Automated trading with intelligent market timing',
      details: [
        { title: 'Minimum Investment $100', text: 'Start automated trading with just $100 — ideal for smart investors entering the AI trading world.' },
        { title: 'Smart Market Timing', text: 'No fixed duration! The AI opens and closes trades based on live Stock & Forex market conditions and timing, ensuring the best opportunities.' },
        { title: 'AI-Driven Decisions', text: 'Advanced algorithms analyze market trends and execute trades automatically for optimal results.' },
        { title: 'Zero Manual Work', text: 'Fully automated system — no charts, no manual entries, no experience required.' },
        { title: 'Risk Management', text: 'Built-in risk controls and capital safety features protect your investment.' }
      ]
    },
    'account': {
      title: 'Trading Accounts',
      description: 'Choose the account type that suits you',
      details: [
        { title: 'AI Auto Trading Account', text: 'Start live trading with AI automation using a minimum deposit of just $100. Perfect for hassle-free trading in real Stock & Forex markets.' },
        { title: 'Standard Account', text: 'Experience simplified AI trading with basic features and automated risk management. Minimum deposit: $500.' },
        { title: 'VIP Account', text: 'Get exclusive benefits, personal manager, and higher returns.' },
        { title: 'Crypto Account', text: 'Trade and deposit using cryptocurrencies like Bitcoin and Ethereum.' },
        { title: 'Multi-Currency', text: 'Hold and trade in multiple currencies including USD, EUR, INR.' }
      ]
    },
    'flex': {
      title: 'Flex Trading',
      description: 'Flexible trading mechanics for advanced traders',
      details: [
        { title: 'Flexible Closing', text: 'Close your trades early to lock in profits or minimize losses.' },
        { title: 'Partial Close', text: 'Close part of your position while keeping the rest open.' },
        { title: 'Rollover Option', text: 'Extend your trade duration if you need more time.' },
        { title: 'Dynamic Returns', text: 'Returns adjust based on market conditions and timing.' },
        { title: 'Advanced Control', text: 'Full control over your trading strategy and risk management.' }
      ]
    },
    'withdrawals': {
      title: 'Withdrawals',
      description: 'Fast and secure withdrawal process',
      details: [
        { title: 'Quick Processing', text: 'Withdrawals processed within 1-3 business days.' },
        { title: 'Multiple Methods', text: 'Withdraw via bank transfer, e-wallets, cards, or crypto.' },
        { title: 'No Hidden Fees', text: 'Transparent fee structure, no surprise charges.' },
        { title: 'Secure Transactions', text: 'Bank-level security for all withdrawal transactions.' },
        { title: 'Minimum Withdrawal', text: 'Withdraw as little as $10 from your account.' }
      ]
    },
    'forex': {
      title: 'Forex Trading',
      description: 'Trade currency pairs 24/5',
      details: [
        { title: '70+ Currency Pairs', text: 'Trade major, minor, and exotic forex pairs.' },
        { title: '24/5 Trading', text: 'Markets open 24 hours a day, 5 days a week.' },
        { title: 'Low Spreads', text: 'Competitive spreads starting from 0.1 pips.' },
        { title: 'High Leverage', text: 'Trade with leverage up to 1:1000 for maximum potential.' },
        { title: 'Classic Mode', text: 'Perfect for both short-term scalping and long-term position trading.' }
      ]
    },
    'islamic': {
      title: 'Islamic Account',
      description: 'Sharia-compliant trading account',
      details: [
        { title: 'Swap-Free Trading', text: 'No overnight interest charges, fully halal and Sharia-compliant.' },
        { title: 'All Assets Available', text: 'Trade forex, stocks, commodities, and indices.' },
        { title: 'Same Features', text: 'Access all platform features available in standard accounts.' },
        { title: 'Instant Execution', text: 'Fast order execution with no delays.' },
        { title: 'Halal Certified', text: 'Verified by Islamic scholars as compliant with Islamic finance principles.' }
      ]
    },
    'promotions': {
      title: 'Promotions & Bonuses',
      description: 'Exclusive offers for our traders',
      details: [
        { title: 'Welcome Bonus', text: 'Get up to 100% bonus on your first deposit.' },
        { title: 'Loyalty Program', text: 'Earn points with every trade and redeem for cash rewards.' },
        { title: 'Cashback Offers', text: 'Get cashback on your trading volume every month.' },
        { title: 'Referral Bonus', text: 'Invite friends and earn rewards for each successful referral.' },
        { title: 'VIP Benefits', text: 'Exclusive tournaments, higher returns, and personal account manager.' }
      ]
    }
  };

  const content = tradingContent[option];

  if (!content) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Header />
        <main className="pt-24 pb-20">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <h1 className="text-4xl font-bold mb-4">Page Not Found</h1>
            <Link to="/trading" className="text-yellow-500 hover:text-yellow-400">
              ← Back to Trading
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <Header />
      
      <main className="pt-24 pb-20">
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-yellow-900/20 to-black"></div>
          <div className="relative z-10 max-w-7xl mx-auto px-6">
            <Link to="/trading" className="text-yellow-500 hover:text-yellow-400 flex items-center mb-6">
              ← Back to Trading
            </Link>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">{content.title}</h1>
            <p className="text-xl text-gray-400 max-w-3xl mb-4">{content.description}</p>
            {content.subtitle && (
              <p className="text-lg text-gray-500 max-w-3xl">{content.subtitle}</p>
            )}
          </div>
        </section>

        {/* Details Section */}
        <section className="max-w-7xl mx-auto px-6 py-20">
          <div className="grid md:grid-cols-2 gap-6">
            {content.details.map((detail, index) => (
              <div key={index} className="bg-gradient-to-br from-gray-900 to-black rounded-xl p-8 border border-gray-800 hover:border-yellow-500 transition-all group">
                {detail.step && (
                  <div className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center text-black font-bold text-xl mb-4">
                    {detail.step}
                  </div>
                )}
                {detail.icon && (
                  <div className="text-5xl mb-4 transform group-hover:scale-110 transition-transform duration-300">
                    {detail.icon}
                  </div>
                )}
                <h3 className="text-2xl font-bold mb-3 text-yellow-500">{detail.title}</h3>
                <p className="text-gray-300 text-lg">{detail.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="max-w-5xl mx-auto px-6 py-20">
          <div className="bg-gradient-to-r from-yellow-900/30 to-gray-900 rounded-2xl p-12 text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Start?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Join thousands of traders and start your journey today
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href={REGISTER_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold text-lg px-8 py-6 rounded-xl">
                  Start Trading Now
                </Button>
              </a>
              <Link to="/contact">
                <Button className="bg-transparent hover:bg-gray-800 text-white font-bold text-lg px-8 py-6 rounded-xl border-2 border-yellow-500">
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default TradingDetailPage;
